// src/features/settings/components/stock-locations-panel/StockLocationsPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useStockLocations } from '../../../inventory/hooks/useStockLocations';
import { useStockLocationPermissions } from '../../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../../hooks/mui/useConfirmDialog';
import StockLocationCreateForm from './StockLocationCreateForm';
import StockLocationsGrid from './StockLocationsGrid';

const EMPTY_NEW_LOCATION = {
  name: '',
  description: '',
};

export default function StockLocationsPanel() {
  const { t } = useTranslation(['settings', 'common']);
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const { canCreate, canUpdate, canDelete } = useStockLocationPermissions();

  const {
    stockLocations,
    loading,
    error,
    clearError,
    fetchStockLocations,
    createStockLocation,
    updateStockLocation,
    deleteStockLocation,
  } = useStockLocations();

  const [addFormVisible, setAddFormVisible] = useState(false);
  const [newLocation, setNewLocation] = useState(EMPTY_NEW_LOCATION);
  const [createErrors, setCreateErrors] = useState({});

  useEffect(() => {
    fetchStockLocations();
  }, [fetchStockLocations]);

  // ── Create form ───────────────────────────────────────────────────────────

  const handleFormChange = useCallback((field, value) => {
    setNewLocation(prev => ({ ...prev, [field]: value }));
    setCreateErrors(prev => ({ ...prev, [field]: null }));
  }, []);

  const handleCreate = async () => {
    const errors = {};
    if (!newLocation.name.trim()) errors.name = t('settings:stock_locations.validation.name_required');
    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);
      return;
    }

    const result = await createStockLocation({
      name: newLocation.name.trim(),
      description: newLocation.description.trim() || null,
    });

    if (result.success) {
      showSuccess(t('settings:stock_locations.success.created'));
      setNewLocation(EMPTY_NEW_LOCATION);
      setAddFormVisible(false);
    } else {
      showError(result.error);
    }
  };

  const handleCancelCreate = useCallback(() => {
    setNewLocation(EMPTY_NEW_LOCATION);
    setCreateErrors({});
    setAddFormVisible(false);
  }, []);

  // ── Active toggle ─────────────────────────────────────────────────────────

  const handleToggleActive = useCallback(async (row) => {
    if (!canUpdate) return;
    const result = await updateStockLocation(row.stock_location_id, {
      name: row.name,
      description: row.description,
      active: !row.active,
    });
    if (!result.success) showError(result.error);
  }, [canUpdate, updateStockLocation, showError]);

  // ── Inline text edit ──────────────────────────────────────────────────────

  const processRowUpdate = useCallback(async (newRow, oldRow) => {
    if (!canUpdate) return oldRow;
    const result = await updateStockLocation(newRow.stock_location_id, {
      name: newRow.name,
      description: newRow.description,
      active: newRow.active,
    });
    if (result.success) return result.data.data;
    showError(result.error);
    return oldRow;
  }, [canUpdate, updateStockLocation, showError]);

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = useCallback((row) => {
    showConfirmDialog({
      title: t('settings:stock_locations.confirm.delete_title'),
      message: t('settings:stock_locations.confirm.delete_message', { name: row.name }),
      severity: 'error',
      onConfirm: async () => {
        const result = await deleteStockLocation(row.stock_location_id);
        if (result.success) {
          showSuccess(t('settings:stock_locations.success.deleted'));
        } else {
          showError(result.error);
        }
      },
    });
  }, [showConfirmDialog, deleteStockLocation, showSuccess, showError, t]);

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('settings:stock_locations.description')}
        </Typography>

        {/* Actions Bar */}
        <Box mb={2} display="flex" gap={1}>
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddFormVisible(true)}
              disabled={addFormVisible}
            >
              {t('settings:stock_locations.buttons.add')}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchStockLocations()}
          >
            {t('common:buttons.refresh')}
          </Button>
        </Box>

        <StockLocationCreateForm
          visible={addFormVisible}
          values={newLocation}
          errors={createErrors}
          loading={loading}
          onChange={handleFormChange}
          onSubmit={handleCreate}
          onCancel={handleCancelCreate}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        <StockLocationsGrid
          stockLocations={stockLocations}
          loading={loading}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onToggleActive={handleToggleActive}
          onDelete={handleDelete}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={(err) => showError(err.message)}
        />
      </Paper>

      <ConfirmDialog />
    </Box>
  );
}
